import { Roles } from "../constants/roles";
import { LikeForm } from "../like/like-form";

import { FormBase } from "../util/form-base-form";

export class ModalForm extends FormBase {
    private readonly likeForm: LikeForm;

    public constructor(likeForm: LikeForm = new LikeForm()) {
        super();
        this.likeForm = likeForm;
    }

    public getElementModal(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.ELEMENT_MODAL}"]`, HTMLElement);
    }

    public getElementModalOverlay(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.ELEMENT_MODAL_OVERLAY}"]`, HTMLElement);
    }

    public getElementModalBody(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.ELEMENT_MODAL_BODY}"]`, HTMLElement);
    }

    public getElementModalFooter(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.ELEMENT_MODAL_FOOTER}"]`, HTMLElement);
    }

    public getElementModalTitle(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.ELEMENT_MODAL_TITLE}"]`, HTMLElement);
    }

    public getElementModalClose(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.ELEMENT_MODAL_CLOSE}"]`, HTMLElement);
    }

    public getPostCreateForm(root: ParentNode): HTMLFormElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_CREATE_FORM}"]`, HTMLFormElement);
    }

    public getPostEditForm(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_EDIT_FORM}"]`, HTMLElement);
    }

    public getVisiblePostEditForm(root: ParentNode): HTMLElement | null {
        const forms = this.queryElements(root, `[data-role="${Roles.POST_EDIT_FORM}"]`, HTMLElement);

        return (
            forms.find((form) => {
                if (!(form instanceof HTMLElement)) {
                    return false;
                }

                const inlineDisplay = form.style.display;
                if (inlineDisplay === "none") {
                    return false;
                }

                return window.getComputedStyle(form).display !== "none";
            }) || null
        );
    }

    public getPostEditFormInner(root: ParentNode): HTMLFormElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_EDIT_FORM_INNER}"]`, HTMLFormElement);
    }

    public getPostEditCancelButton(root: ParentNode): HTMLButtonElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_EDIT_CANCEL}"]`, HTMLButtonElement);
    }

    public getVisiblePostEditCancelButton(root: ParentNode): HTMLButtonElement | null {
        const visibleForm = this.getVisiblePostEditForm(root);
        if (!visibleForm) {
            return null;
        }

        return this.getPostEditCancelButton(visibleForm);
    }

    public getClosestPostCreateForm(target: Element | null | undefined): HTMLFormElement | null {
        return this.closest(target, `[data-role="${Roles.POST_CREATE_FORM}"]`, HTMLFormElement);
    }

    public getClosestPostEditFormInner(target: Element | null | undefined): HTMLFormElement | null {
        return this.closest(target, `[data-role="${Roles.POST_EDIT_FORM_INNER}"]`, HTMLFormElement);
    }

    public getClosestPostIdElement(target: Element | null | undefined): HTMLElement | null {
        return this.closest(target, "[data-post-id]", HTMLElement);
    }

    public getClosestPostContainer(target: Element | null | undefined): HTMLElement | null {
        return this.closest(target, '.board-card, [data-role="post-detail"]', HTMLElement);
    }

    public getClosestPostCard(target: Element | null | undefined): HTMLElement | null {
        return this.closest(target, `[data-role="${Roles.POST_CARD}"]`, HTMLElement);
    }

    public getClosestPostDetail(target: Element | null | undefined): HTMLElement | null {
        return this.closest(target, `[data-role="${Roles.POST_DETAIL}"]`, HTMLElement);
    }

    public getPostDetail(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, ".post-detail[data-post-id]", HTMLElement);
    }

    public getPostDetailById(postId: number, root: ParentNode = document): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.POST_DETAIL}"][data-post-id="${String(postId)}"]`,
            HTMLElement,
        );
    }

    public getPostDetailByModalContentId(modalContentId: string, root: ParentNode = document): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.POST_DETAIL}"][data-modal-content-id="${String(modalContentId)}"]`,
            HTMLElement,
        );
    }

    public getWrappedPostDetail(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, ".modal-content-wrapper .post-detail[data-post-id]", HTMLElement);
    }

    public getBoardIdInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, 'input[name="board_id"]');
    }

    public getRemovedAttachedImagesInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, 'input[name="removed_attached_images"]');
    }

    public getRemovedAttachedVideosInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, 'input[name="removed_attached_videos"]');
    }

    public getTitleInput(root: ParentNode): HTMLInputElement | HTMLTextAreaElement | null {
        return this.getInput(root, 'input[name="post_title"]') || this.getTextarea(root, 'textarea[name="post_title"]');
    }

    public getContentTextarea(root: ParentNode): HTMLTextAreaElement | null {
        return this.getTextarea(root, 'textarea[name="content"]');
    }

    public getProductIdInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, `[data-role="${Roles.POST_PRODUCT_ID}"]`);
    }

    public getSnsLinkInputs(root: ParentNode): HTMLInputElement[] {
        return this.queryElements(root, `[data-role="${Roles.SNS_LINK_INPUT}"]`, HTMLInputElement);
    }

    public getFileInputs(root: ParentNode): HTMLInputElement[] {
        return this.queryElements(
            root,
            `[data-role="${Roles.IMAGE_UPLOAD}"], [data-role="${Roles.VIDEO_UPLOAD}"]`,
            HTMLInputElement,
        );
    }

    public getWysiwygEditors(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.WYSIWYG_EDITOR}"]`, HTMLElement);
    }

    public getWysiwygToolbars(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.WYSIWYG_TOOLBAR}"]`, HTMLElement);
    }

    public getYouTubePreviews(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.YOUTUBE_PREVIEW}"]`, HTMLElement);
    }

    public getClosestYouTubePlayButton(target: EventTarget | null): HTMLElement | null {
        return this.closest(target, `[data-role="${Roles.YOUTUBE_PLAY_BUTTON}"]`, HTMLElement);
    }

    public getClosestYouTubePreview(target: Element | null | undefined): HTMLElement | null {
        return this.closest(target, `[data-role="${Roles.YOUTUBE_PREVIEW}"]`, HTMLElement);
    }

    public getPostLikeButton(root: ParentNode): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.LIKE_BUTTON}"][data-like-scope="post"], ` +
                `[data-role="${Roles.LIKE_BUTTON}"][data-post-id]:not([data-comment-id])`,
            HTMLElement,
        );
    }

    public getCommentLikeButton(root: ParentNode, commentId: number): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.LIKE_BUTTON}"][data-like-scope="comment"][data-comment-id="${String(commentId)}"]`,
            HTMLElement,
        );
    }

    public getPostLikeCountElement(root: ParentNode): HTMLElement | null {
        const likeButton = this.getPostLikeButton(root);
        return likeButton ? this.likeForm.getCountElement(likeButton) : null;
    }

    public getPostLikeIconElement(root: ParentNode): HTMLElement | null {
        const likeButton = this.getPostLikeButton(root);
        return likeButton ? this.likeForm.getIconElement(likeButton) : null;
    }

    public getCommentActionCountElement(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.COMMENT_COUNT_ACTION}"]`, HTMLElement);
    }

    public getCommentCountElements(root: ParentNode): HTMLElement[] {
        return this.queryElements(
            root,
            `[data-role="${Roles.COMMENT_COUNT_ACTION}"], [data-role="${Roles.COMMENT_COUNT_HEADER}"]`,
            HTMLElement,
        );
    }

    public getReadCountElement(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.READ_COUNT}"]`, HTMLElement);
    }

    public getSnsContent(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.SNS_CONTENT}"]`, HTMLElement);
    }

    public getPostDetailContent(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_DETAIL_CONTENT}"]`, HTMLElement);
    }

    public getPostDetailModalFooter(root: ParentNode): HTMLElement | null {
        const found = this.queryElement(root, ".post-detail-layout > .post-detail-modal-footer", HTMLElement);
        if (found) return found;
        // When root itself is .post-detail-layout (fetched SKIP-mode content), footer is a direct child.
        if (root instanceof HTMLElement && root.classList.contains("post-detail-layout")) {
            const direct = this.queryElement(root, ":scope > .post-detail-modal-footer", HTMLElement);
            if (direct) return direct;
        }
        return null;
    }

    public getPostDetailLayout(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".post-detail-layout", HTMLElement);
    }

    public getClosestPostDetailLayout(target: Element): HTMLElement | null {
        return this.closest(target, ".post-detail-layout", HTMLElement);
    }

    public getPostDisplayContent(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_CONTENT_DISPLAY}"]`, HTMLElement);
    }

    public getPostContent(root: ParentNode): HTMLElement | null {
        return this.getPostDetailContent(root) || this.getPostDisplayContent(root);
    }

    public getPostCardTitle(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_CARD_TITLE}"]`, HTMLElement);
    }

    public getBoardMediaArea(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.BOARD_MEDIA_AREA}"]`, HTMLElement);
    }

    public getBoardImagesGrid(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.BOARD_IMAGES_GRID}"]`, HTMLElement);
    }

    public getBoardVideoWrap(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.BOARD_VIDEO_WRAP}"]`, HTMLElement);
    }

    public getBoardMediaPlaceholder(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.BOARD_MEDIA_PLACEHOLDER}"]`, HTMLElement);
    }

    public getOgpOrSnsPreview(root: ParentNode): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.OGP_PREVIEW_CARD}"], [data-role="${Roles.SNS_PREVIEW_TILES_GRID}"], [data-role="${Roles.SNS_PREVIEW_TILE}"]`,
            HTMLElement,
        );
    }

    public getTitleDataElements(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, "[data-post-title]", HTMLElement);
    }

    public getFirstTitleDataElement(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, "[data-post-title]", HTMLElement);
    }

    public getCreateTemplateByModalContentId(
        modalContentId: string,
        root: ParentNode = document,
    ): HTMLTemplateElement | null {
        return this.queryElement(
            root,
            `template[data-modal-content-id="${String(modalContentId)}"]`,
            HTMLTemplateElement,
        );
    }

    public getAllElements(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, "*", HTMLElement);
    }

    public getWysiwygTextareas(root: ParentNode): HTMLTextAreaElement[] {
        return this.queryElements(root, 'textarea[data-wysiwyg="true"]', HTMLTextAreaElement);
    }

    public getInvalidElements(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, ".is-invalid", HTMLElement);
    }

    public hasInvalidElement(root: ParentNode): boolean {
        return this.getInvalidElements(root).length > 0;
    }

    public getIframe(root: ParentNode): HTMLIFrameElement | null {
        return this.queryElement(root, "iframe", HTMLIFrameElement);
    }

    public hasImage(root: ParentNode): boolean {
        return this.queryElement(root, "img", HTMLImageElement) !== null;
    }

    public hasMeaningfulMedia(root: ParentNode): boolean {
        return this.queryElement(root, "img, video, iframe, audio, source", HTMLElement) !== null;
    }

    public getClosestPostEditFormWrapper(target: Element | null | undefined): HTMLElement | null {
        return this.closest(target, ".post-edit-form", HTMLElement);
    }

    public getClosestAnchor(target: Element | null | undefined): HTMLAnchorElement | null {
        return this.closest(target, "a", HTMLAnchorElement);
    }

    public getClosestButton(target: Element | null | undefined): HTMLButtonElement | null {
        return this.closest(target, "button", HTMLButtonElement);
    }

    // <summary> handles its own native open/close toggle on click; the card-open handler
    // must not preventDefault() there or the <details> element can never be expanded.
    public getClosestSummary(target: Element | null | undefined): HTMLElement | null {
        return this.closest(target, "summary", HTMLElement);
    }

    public getPostSnsLinksDataElements(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.POST_SNS_LINKS_DATA}"]`, HTMLElement);
    }

    private findFirstElementOutsideModal(selector: string): HTMLElement | null {
        const modal = this.getElementModal();
        const elements = this.queryElements(document, selector, HTMLElement);

        for (const element of elements) {
            if (!modal || !modal.contains(element)) {
                return element;
            }
        }

        return null;
    }

    public findParentPostCard(postId: number): HTMLElement | null {
        return this.findFirstElementOutsideModal(`[data-role="${Roles.POST_CARD}"][data-post-id="${String(postId)}"]`);
    }

    public getParentCommentLikeButton(commentId: number): HTMLElement | null {
        return this.findFirstElementOutsideModal(
            `[data-role="${Roles.LIKE_BUTTON}"][data-comment-id="${String(commentId)}"]`,
        );
    }
}
